import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  ChevronDown,
  ChevronUp,
  FileType2,
  FileDown,
  Braces,
  Package,
  GraduationCap,
  X
} from 'lucide-react';
import { useCourseExports } from '@/hooks/useCourseExports';
import { capitalizeFirst } from '@/lib/utils';

/**
 * CoursePreviewModal Component
 *
 * A read-only modal for previewing full course details from the history page.
 * Displays course metadata, learning objectives, topics, and activities.
 * Includes export functionality.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onClose - Called when modal should close
 * @param {Object|null} props.course - Full course data (null during loading)
 * @param {boolean} props.isLoading - Shows loading state
 * @param {string} props.courseId - Course ID (needed for eClass export)
 */
export default function CoursePreviewModal({
  isOpen,
  onClose,
  course,
  isLoading,
  courseId
}) {
  // Local state for expandable sections
  const [goalsExpanded, setGoalsExpanded] = useState(true);
  const [expandedTopics, setExpandedTopics] = useState(new Set());

  // Export functionality - pass courseId and course data directly
  const {
    handleExportJson,
    handleExportCdm,
    handleExportDoc,
    handleExportPdf,
    handleExportEclass
  } = useCourseExports(courseId, course);

  const toggleTopic = (topicId) => {
    setExpandedTopics(prev => {
      const next = new Set(prev);
      if (next.has(topicId)) {
        next.delete(topicId);
      } else {
        next.add(topicId);
      }
      return next;
    });
  };

  const toggleGoals = () => {
    setGoalsExpanded(prev => !prev);
  };

  // Calculate totals
  const totalGoals = course?.learningObjectives?.length || course?.goals?.length || 0;
  const totalTopics = course?.topics?.length || 0;
  const totalActivities = (course?.topics || []).reduce(
    (sum, t) => sum + (t.activities?.length || 0), 0
  );

  // Get learning objectives (support both field names)
  const learningObjectives = course?.learningObjectives || course?.goals || [];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0 gap-0">
        {isLoading ? (
          // Loading state
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading course details...</p>
            </div>
          </div>
        ) : course ? (
          <>
            {/* Header */}
            <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-orange-50 via-pink-50 to-teal-50">
              <DialogTitle className="text-2xl font-bold text-gray-900 pr-8">
                {course.title}
              </DialogTitle>
              <DialogDescription asChild>
                <div className="space-y-2">
                  {/* Metadata badges */}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {course.subjectArea && (
                      <span className="text-sm px-3 py-1 rounded-full bg-orange-100 text-orange-700 font-medium">
                        {course.subjectArea}
                      </span>
                    )}
                    {course.educationalLevel && (
                      <span className="text-sm px-3 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">
                        {course.educationalLevel}
                      </span>
                    )}
                    {course.grade && (
                      <span className="text-sm px-3 py-1 rounded-full bg-purple-100 text-purple-700 font-medium">
                        Grade: {course.grade}
                      </span>
                    )}
                    {course.totalHours > 0 && (
                      <span className="text-sm px-3 py-1 rounded-full bg-teal-100 text-teal-700 font-medium">
                        {course.totalHours} hours
                      </span>
                    )}
                  </div>
                  {/* Stats */}
                  <div className="flex items-center gap-2 text-xs">
                    <span className="px-2 py-1 rounded-full bg-orange-100 text-orange-700 font-semibold">
                      {totalGoals} L.O.
                    </span>
                    <span className="px-2 py-1 rounded-full bg-teal-100 text-teal-700 font-semibold">
                      {totalTopics} Topics
                    </span>
                    <span className="px-2 py-1 rounded-full bg-pink-100 text-pink-700 font-semibold">
                      {totalActivities} Activities
                    </span>
                  </div>
                </div>
              </DialogDescription>
            </DialogHeader>

            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
              {/* Description */}
              {course.description && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Description
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {course.description}
                  </p>
                </div>
              )}

              {/* Learning Objectives */}
              {learningObjectives.length > 0 && (
                <div className="border-l-4 border-orange-500 bg-orange-50 rounded-r-xl overflow-hidden">
                  <div
                    className="flex items-center justify-between pl-4 pr-4 py-3 hover:bg-orange-100 transition-colors cursor-pointer"
                    onClick={toggleGoals}
                  >
                    <h3 className="text-lg font-semibold text-gray-900">Learning Objectives</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-orange-700 font-medium">
                        {learningObjectives.length} objectives
                      </span>
                      {goalsExpanded ? (
                        <ChevronUp className="text-orange-600" size={24} />
                      ) : (
                        <ChevronDown className="text-orange-600" size={24} />
                      )}
                    </div>
                  </div>
                  <div className={`${goalsExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'} transition-all duration-300 overflow-hidden`}>
                    <ul className="space-y-2 px-4 pb-4">
                      {learningObjectives.map((goal, index) => (
                        <li key={goal.id} className="flex items-start gap-3 p-3 bg-gradient-to-r from-orange-50 to-pink-50 rounded-xl">
                          <span className="flex-shrink-0 w-7 h-7 bg-gradient-to-br from-orange-500 to-pink-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                            {index + 1}
                          </span>
                          <span className="flex-1 text-gray-800">
                            {goal.text}
                          </span>
                          {goal.bloomLevel && (
                            <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700 font-medium">
                              {goal.bloomLevel}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Topics & Activities */}
              {course.topics && course.topics.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    Topics & Activities
                  </h3>
                  <div className="space-y-4">
                    {course.topics.map((topic) => {
                      const isExpanded = expandedTopics.has(topic.id);
                      return (
                        <div key={topic.id} className="border-l-4 border-teal-500 bg-teal-50 rounded-r-xl overflow-hidden">
                          <div
                            className="flex items-center justify-between pl-4 pr-4 py-4 hover:bg-teal-100 transition-colors cursor-pointer"
                            onClick={() => toggleTopic(topic.id)}
                          >
                            <h4 className="font-bold text-gray-900 text-lg">{topic.title}</h4>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-teal-700 font-medium">
                                {topic.activities?.length || 0} activities
                              </span>
                              {isExpanded ? (
                                <ChevronUp className="text-teal-600" size={24} />
                              ) : (
                                <ChevronDown className="text-teal-600" size={24} />
                              )}
                            </div>
                          </div>
                          <div className={`${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'} transition-all duration-300 overflow-hidden`}>
                            <ul className="space-y-3 px-4 pb-4">
                              {(topic.activities || []).map((activity) => {
                                // Support both new (objectiveId) and legacy (goalId) field names
                                const activityObjectiveId = activity.objectiveId || activity.goalId;
                                const goalIndex = learningObjectives.findIndex(g => g.id === activityObjectiveId);
                                const goalText = learningObjectives.find(g => g.id === activityObjectiveId)?.text;

                                return (
                                  <li key={activity.id} className="bg-white p-4 rounded-xl shadow-sm">
                                    {/* Activity header with badges */}
                                    <div className="flex items-start gap-2 mb-2 flex-wrap">
                                      <span className="text-orange-700 font-bold text-base">
                                        {activity.title}
                                      </span>
                                      {typeof goalIndex === 'number' && goalIndex > -1 && (
                                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-semibold">
                                          L.O. {goalIndex + 1}
                                        </span>
                                      )}
                                      {activity.actor && (
                                        <span className="text-xs bg-pink-100 text-pink-700 px-2 py-1 rounded-full font-medium">
                                          {capitalizeFirst(activity.actor)}
                                        </span>
                                      )}
                                      {activity.duration && (
                                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                                          {activity.duration} mins
                                        </span>
                                      )}
                                      {activity.resourceType && (
                                        <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full font-medium">
                                          {activity.resourceType}
                                        </span>
                                      )}
                                    </div>

                                    {/* Description */}
                                    {activity.description && (
                                      <p className="text-gray-700 text-sm mb-2">
                                        {activity.description}
                                      </p>
                                    )}

                                    {/* Goal reference */}
                                    {goalText && (
                                      <div className="mb-2 p-2 bg-purple-50 border-l-2 border-purple-400 rounded-r">
                                        <p className="text-xs text-purple-700">
                                          <span className="font-semibold">Learning Objective:</span> {goalText}
                                        </p>
                                      </div>
                                    )}

                                    {/* Facilitator role */}
                                    {activity.facilitatorRole && (
                                      <div className="mb-2">
                                        <p className="text-xs text-gray-500 font-semibold mb-1">Facilitator Role</p>
                                        <p className="text-gray-700 text-sm italic">
                                          {activity.facilitatorRole}
                                        </p>
                                      </div>
                                    )}

                                    {/* Resource info */}
                                    {(activity.resourceTitle || activity.resourceDescription) && (
                                      <div className="border-t pt-2 mt-2">
                                        <p className="text-xs text-gray-500 font-semibold mb-1">Resource</p>
                                        {activity.resourceTitle && (
                                          <span className="text-gray-800 text-sm font-medium">
                                            {activity.resourceTitle}
                                          </span>
                                        )}
                                        {activity.resourceDescription && (
                                          <p className="text-gray-600 text-sm italic">
                                            {activity.resourceDescription}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Footer with export buttons */}
            <DialogFooter className="px-6 py-4 border-t bg-gray-50 flex-wrap gap-2">
              <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                <Button
                  onClick={handleExportDoc}
                  disabled={!course}
                  size="sm"
                  className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white"
                >
                  <FileType2 size={16} className="mr-1" /> Word
                </Button>
                <Button
                  onClick={handleExportPdf}
                  disabled={!course}
                  size="sm"
                  className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white"
                >
                  <FileDown size={16} className="mr-1" /> PDF
                </Button>
                <Button
                  onClick={handleExportJson}
                  disabled={!course}
                  size="sm"
                  className="bg-gradient-to-r from-slate-500 to-gray-600 hover:from-slate-600 hover:to-gray-700 text-white"
                >
                  <Braces size={16} className="mr-1" /> JSON
                </Button>
                <Button
                  onClick={handleExportEclass}
                  disabled={!course}
                  size="sm"
                  className="bg-gradient-to-r from-orange-500 to-purple-500 hover:from-orange-600 hover:to-purple-600 text-white"
                >
                  <GraduationCap size={16} className="mr-1" /> eClass
                </Button>
              </div>
            </DialogFooter>
          </>
        ) : (
          // Error state
          <div className="text-center py-16">
            <div className="text-gray-400 mb-4">
              <X size={48} className="mx-auto" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Failed to load course</h3>
            <p className="text-gray-500">Please try again or close this dialog.</p>
            <Button
              onClick={onClose}
              className="mt-4"
              variant="outline"
            >
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
